import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import { MoreHorizontal, Pencil, Search, LucideInfo } from "lucide-react";
import Breadcrumbs from "../../coreComponents/breadcrumbs";
import CommonCardHeader from "../../coreComponents/CommonCardHeader";
import { routes } from "../../routers/routes";
import LoadingComponent from "../lodingComponent";
import type { ReactNode } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { usePagination } from "../../hooks/usePagination";
import LeftIcon from "../svg/LeftButton";
import RightIcon from "../svg/RightIcon";

const DISPLAY_KEYS = [
  "fullName",
  "role",
  "email",
  "mobileNumber",
  "country",
  "state",
  "city",
  "district",
  "village",
  "pincode",
  "createdDateTime",
] as const;

type UserRow = Record<string, unknown> & {
  id?: string | number;
};

const AllUsersPage = () => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState<string[]>([]);

  const {
    data: usersData,
    loading,
    pageNumber,
    totalPages,
    searchQuery,
    setSearchQuery,
    canGoToNextPage,
    canGoToPreviousPage,
    goToNextPage,
    goToPreviousPage,
  } = usePagination({
    endpoint: "/users",
    pageSize: 10,
    enabled: true,
    filterOn: "fullName", // ✅ API: filterOn=fullName&filterQuery=<search>
  });

  const users = useMemo(() => usersData as UserRow[], [usersData]);

  const formatHeader = (key: string) =>
    key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

  const formatCell = (value: unknown): string => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const openUserLookup = (row: UserRow) => {
    navigate(routes.UserLookup, {
      state: { mobileNumber: String(row.mobileNumber ?? "") },
    });
  };

  useEffect(() => {
    if (users.length > 0) {
      const keys = Object.keys(users[0]);
      const ordered = [...DISPLAY_KEYS].filter((k) => keys.includes(k));
      setColumns(ordered);
      return;
    }
    setColumns([]);
  }, [users]);

  return (
    <>
      <Breadcrumbs mainTitle="All users" parent="Users" />
      <Container fluid>
        <Row>
          <Col sm="12" className="tw-px-0">
            <Card>
              <CommonCardHeader title="All users" headClass="pb-2" />
              <CardBody className="pt-0">
                {/* ── Search bar ── */}
                <div style={{ position: "relative", width: "250px", marginBottom: "12px" }}>
  <span style={{
    position: "absolute",
    left: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#888",
    pointerEvents: "none",
    zIndex: 1,
    display: "flex",
  }}>
    <Search size={16} />
  </span>
  <Input
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    placeholder="Search by full name"
    style={{ paddingLeft: "36px" }}
  />
</div>
                {loading ? (
                  <LoadingComponent />
                ) : users.length === 0 ? (
                  <p className="f-m-light mt-2 mb-0">No users found.</p>
                ) : (
                  <>
                    <Table
                      wrapperClassName="table-responsive rounded"
                      className="table table-hover align-middle text-center mb-0"
                    >
                      <TableHeader className="bg-light-primary">
                        <TableRow>
                          {columns.map((col) => (
                            <TableHead
                              key={col}
                              className="table-header text-center text-nowrap"
                            >
                              {formatHeader(col)}
                            </TableHead>
                          ))}
                          <TableHead className="table-header text-center text-nowrap">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>

                      <TableBody className="table-body">
                        {users.map((row: UserRow, idx: number) => (
                          <TableRow
                            key={String(row.id ?? `user-${idx}`)}
                            style={{ cursor: "pointer" }}
                          >
                            {columns.map((col) => (
                              <TableCell
                                key={col}
                                className="text-nowrap align-middle"
                                title={formatCell(row[col])}
                              >
                                {formatCell(row[col])}
                              </TableCell>
                            ))}

<TableCell className="text-center align-middle d-flex">
  <button
    type="button"
    className="btn p-1"
    onClick={() => navigate(routes.UserDashboard, { state: { userId: row.id } })}
    title="User lookup"
  >
    <LucideInfo size={14} />
  </button>
  <button
    type="button"
    className="btn p-1 ms-1"
    onClick={() => navigate(`${routes.UserEditBase}/${row.id}`)}
    disabled={!row.id}
    title="Edit user"
  >
    <Pencil size={14} />
  </button>
</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* ── Pagination ── */}
                    <div
                      className="d-flex justify-content-end align-items-center gap-2"
                      style={{ marginTop: "10px" }}
                    >
                      <p className="mb-0">
                        Page {pageNumber} of {totalPages}
                      </p>
                      <button
                        onClick={goToPreviousPage}
                        disabled={!canGoToPreviousPage}
                        className="icon-button"
                      >
                        <LeftIcon className="icon" />
                      </button>
                      <button
                        onClick={goToNextPage}
                        disabled={!canGoToNextPage}
                        className={`icon-button ${!canGoToNextPage ? "disabled" : ""}`}
                      >
                        <RightIcon className="icon" />
                      </button>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AllUsersPage;
